import React, { useMemo } from 'react';

// Per-measurement chart matching the app's watch-details graphs, two modes:
//
//  drift — each timing run is a segment of measurement points (cumulative
//          seconds vs the run's first measurement); segment slope = rate.
//  rate  — the app's FilteredRateChart semantics: for each consecutive
//          measurement pair within a run, the pairwise rate
//          (Δoffset / Δdays) in seconds/day, plotted per run.
//
// Axis treatment mirrors the app's fl_chart defaults: a left gutter with
// numeric labels on "nice"-interval gridlines, a unit marker, and date
// ticks along the bottom. Gridlines live in the stretched SVG (horizontal
// lines survive preserveAspectRatio=none); labels are HTML overlays
// (SVG text would distort) positioned in px — the height is fixed, only
// the width stretches.
const W = 320, H = 132, PADL = 34, PADR = 10, PADY = 12;
const MIN_PAIR_DAYS = 0.01; // ~15 min — guards rate blowups from tiny gaps
const NICE_STEPS = [0.1, 0.2, 0.5, 1, 2, 5, 10, 15, 30, 60, 120, 300];

function toSegments(runs) {
  return (runs || [])
    .filter(r => Array.isArray(r.points) && r.points.length >= 2)
    .map(r =>
      r.points
        .map(p => ({ t: new Date(p.t).getTime(), off: Number(p.off) }))
        .filter(p => Number.isFinite(p.t) && Number.isFinite(p.off))
        .sort((a, b) => a.t - b.t)
    )
    .filter(seg => seg.length >= 2);
}

function toRateSegments(driftSegs) {
  return driftSegs
    .map(seg => {
      const pts = [];
      for (let j = 1; j < seg.length; j++) {
        const days = (seg[j].t - seg[j - 1].t) / 86400000;
        if (days < MIN_PAIR_DAYS) continue;
        pts.push({ t: seg[j].t, off: (seg[j].off - seg[j - 1].off) / days });
      }
      return pts;
    })
    .filter(seg => seg.length >= 1);
}

// Gridline values at a "nice" step producing 3–6 lines across [lo, hi].
function niceTicks(lo, hi) {
  const span = Math.max(hi - lo, 0.5);
  const step = NICE_STEPS.find(s => span / s <= 5) || NICE_STEPS[NICE_STEPS.length - 1];
  const ticks = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + 1e-9; v += step) {
    ticks.push(Math.round(v * 100) / 100);
  }
  return ticks;
}

const fmtTick = v => `${v > 0 ? '+' : ''}${Number.isInteger(v) ? v : v.toFixed(1)}`;
const fmtDay = t => new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

export default function ReportRunChart({ runs, audio, mode = 'drift', fallback = null }) {
  const driftSegs = useMemo(() => toSegments(runs), [runs]);
  const segs = useMemo(
    () => (mode === 'rate' ? toRateSegments(driftSegs) : driftSegs),
    [driftSegs, mode]
  );
  // Acoustic timegrapher measurements are instantaneous rates — they only
  // exist on the rate axis (there is no offset series for them). Hollow
  // markers, matching the app's distinct-dot treatment for audio.
  const audioPts = useMemo(
    () =>
      mode === 'rate'
        ? (audio || [])
            .map(a => ({ t: new Date(a.at).getTime(), off: Number(a.rate_spd) }))
            .filter(p => Number.isFinite(p.t) && Number.isFinite(p.off))
        : [],
    [audio, mode]
  );

  if (!driftSegs.length) return fallback;
  if (!segs.length && !audioPts.length) return fallback;

  const all = segs.flat().concat(audioPts);
  const tLo = Math.min(...all.map(p => p.t));
  const tHi = Math.max(...all.map(p => p.t));
  const oLo = Math.min(0, ...all.map(p => p.off));
  const oHi = Math.max(0, ...all.map(p => p.off));
  const tSpan = Math.max(tHi - tLo, 1);
  const oSpan = Math.max(oHi - oLo, 0.5);
  const x = t => PADL + ((t - tLo) / tSpan) * (W - PADL - PADR);
  const y = o => H - PADY - ((o - oLo) / oSpan) * (H - 2 * PADY);
  const ticks = niceTicks(oLo, oHi);
  const tMid = tLo + tSpan / 2;
  const showMid = tSpan > 3 * 86400000; // a middle date tick needs room

  return (
    <div className="rpt-chartwrap">
      <span className="rpt-axisunit">{mode === 'rate' ? 's/day' : 'seconds'}</span>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none">
        {ticks.map(v => (
          <line key={v} x1={PADL} y1={y(v)} x2={W - PADR} y2={y(v)}
            stroke="currentColor" strokeOpacity={v === 0 ? 0.22 : 0.08} strokeWidth="1"
            strokeDasharray={v === 0 ? '3 4' : undefined} />
        ))}
        {segs.map((seg, i) => (
          <g key={i}>
            {seg.length >= 2 && (
              <polyline
                points={seg.map(p => `${x(p.t).toFixed(1)},${y(p.off).toFixed(1)}`).join(' ')}
                fill="none" stroke="var(--rpt-brass)" strokeWidth="1.6" strokeLinejoin="round" />
            )}
            {seg.map((p, j) => (
              <circle key={j} cx={x(p.t).toFixed(1)} cy={y(p.off).toFixed(1)} r="2.2"
                fill="var(--rpt-brass)" stroke="var(--rpt-card)" strokeWidth="1" />
            ))}
          </g>
        ))}
        {audioPts.map((p, j) => (
          <circle key={`a${j}`} cx={x(p.t).toFixed(1)} cy={y(p.off).toFixed(1)} r="3"
            fill="var(--rpt-card)" stroke="var(--rpt-brass)" strokeWidth="1.6" />
        ))}
      </svg>
      {/* y labels: height is a fixed px attribute, so px positioning is exact */}
      {ticks.map(v => (
        <span key={v} className="rpt-ytick" style={{ top: `${y(v) - 7}px` }}>{fmtTick(v)}</span>
      ))}
      <div className="rpt-xticks">
        <span>{fmtDay(tLo)}</span>
        {showMid && <span>{fmtDay(tMid)}</span>}
        <span>{fmtDay(tHi)}</span>
      </div>
    </div>
  );
}
